import { AnnotationsSummary, AtccQcStatistics, ContigStatistics, Genome } from "../api";

/** A Genome will have QC and other stats on it from One Codex jobs, and _may_ have similar external data from ATCC.
 * This function will return the external ATCC version of the data if it exists, and default to One Codex data otherwise.
 */
export function getGenomeStats(genome: Genome): Stats | null {
  // if this field is present, we have ATCC data
  if (genome.attributes?.atcc_metadata?.qc_statistics) {
    return getAtccStats(genome.attributes.atcc_metadata.qc_statistics);
  }
  return getOcxStats(genome);
}

function getAtccStats(atccQcStats: AtccQcStatistics): Stats {
  return {
    annotationStats: getAtccAnnotationsSummary(atccQcStats),
    sequencingStats: getAtccSequencingStats(atccQcStats),
    qualityControl: getAtccQualityControlData(atccQcStats),
  };
}

function getOcxStats(genome: Genome): Stats | null {
  const { primary_assembly } = genome;
  if (primary_assembly === undefined) {
    return null;
  }

  return {
    annotationStats: getOcxAnnotationsSummary(genome),
    sequencingStats: getOcxSequencingStats(genome),
    qualityControl: getOcxQualityControlData(genome),
  };
}

function getOcxAnnotationsSummary(genome: Genome): Stats["annotationStats"] {
  const annotationSet = genome.primary_assembly?.primary_annotation_set;
  let annotationsSummary = undefined;
  if (annotationSet) {
    annotationsSummary = {
      number_of_cds: annotationSet!.coding_gene_counts.num_cds,
      number_of_hypothetical: annotationSet!.coding_gene_counts.num_hypothetical,
      number_of_16s_rrna: annotationSet!.rna_gene_counts.num_16s,
      number_of_23s_rrna: annotationSet!.rna_gene_counts.num_23s,
      number_of_5s_rrna: annotationSet!.rna_gene_counts.num_5s,
      number_of_trna: annotationSet!.rna_gene_counts.num_trna,
      // if the annotations come from OCX, the number of BUSCO annotations is
      // the same as the number of CDS (because we "annotate" using BUSCO)
      number_of_busco_annotations: annotationSet!.coding_gene_counts.num_cds,
    };
  }
  return annotationsSummary;
}

function getOcxSequencingStats(genome: Genome): Stats["sequencingStats"] {
  let sequencingStats = undefined;
  if (
    Object.keys(
      genome.primary_assembly?.derived_metadata?.qc_statistics?.sequencing_statistics || {}
    ).length > 0
  ) {
    sequencingStats = genome.primary_assembly?.derived_metadata?.qc_statistics
      ?.sequencing_statistics as DisplayedSequencingStats;
  }
  return sequencingStats;
}

function getOcxQualityControlData(genome: Genome): Stats["qualityControl"] {
  if (genome.primary_assembly === null) {
    return undefined;
  }
  const stats = genome.primary_assembly.derived_metadata.qc_statistics;

  const contigStatistics = stats.assembly_statistics?.filtered?.contig_statistics || [];
  const qualityControl: QualityControlData = {
    sequencing: {
      trimmedReads: stats.sequencing_statistics?.illumina?.reads?.total_reads ?? null,
      medianQScore: stats.sequencing_statistics?.illumina?.reads?.median_quality ?? null,
      medianQScorePercent: getIlluminaMedianQScorePercentage(
        Object.values(
          (stats.sequencing_statistics?.illumina?.reads?.position_specific_quality_scores as {
            [key: string]: number;
          }) || {}
        )
      ),
      ambiguousContent: getAmbiguousContent(contigStatistics),
    },
    assembly: {
      completeness:
        stats.checkm_results?.completeness ?? stats.busco_results?.busco_completeness ?? null,
      contamination: stats.checkm_results?.contamination ?? null,
      illuminaDepth: stats.sequencing_statistics?.illumina?.depth?.mean ?? null,
      ontDepth: stats.sequencing_statistics?.ont?.depth.mean ?? null,
    },
  };
  return qualityControl;
}

function getAtccAnnotationsSummary(atccQcStats: AtccQcStatistics): Stats["annotationStats"] {
  const annotationStats =
    Object.keys(atccQcStats?.annotations_summary || {}).length > 0
      ? atccQcStats?.annotations_summary
      : undefined;

  return annotationStats;
}

function getAtccSequencingStats(atccQcStats: AtccQcStatistics): Stats["sequencingStats"] {
  const inputReadsSummary = atccQcStats.input_reads_summary;
  const sequencingStats: DisplayedSequencingStats = {
    illumina: {
      reads: {
        total_reads: inputReadsSummary.illumina_read_count,
        median_quality: inputReadsSummary.illumina_median_q,
      },
      depth: {
        mean: inputReadsSummary.illumina_mean_cov_depth,
      },
    },
  };

  if (inputReadsSummary.ont_read_count) {
    sequencingStats.ont = {
      reads: {
        total_reads: inputReadsSummary.ont_read_count,
        median_quality: inputReadsSummary.ont_median_q!,
      },
    };
  }

  return sequencingStats;
}

function getAtccQualityControlData(atccQcStats: AtccQcStatistics): Stats["qualityControl"] {
  const { illumina_sequencing_quality_control } = atccQcStats.sequencing_quality_control;
  const assemblyQualityControlData = atccQcStats.assembly_quality_control;

  let ambiguous_content_percent =
    illumina_sequencing_quality_control.ambiguous_content_percent ?? null;
  if (ambiguous_content_percent !== null) {
    ambiguous_content_percent = ambiguous_content_percent / 100;
  }

  let median_q_score_percent = illumina_sequencing_quality_control.median_q_score_percent ?? null;
  if (median_q_score_percent !== null) {
    median_q_score_percent = median_q_score_percent / 100;
  }

  return {
    sequencing: {
      trimmedReads: illumina_sequencing_quality_control.number_of_trimmed_reads || null,
      medianQScore: illumina_sequencing_quality_control.median_q_score || null,
      medianQScorePercent: median_q_score_percent,
      ambiguousContent: ambiguous_content_percent,
    },
    assembly: {
      completeness: assemblyQualityControlData?.genome_completeness ?? null,
      contamination: assemblyQualityControlData?.genome_contamination ?? null,
      illuminaDepth: assemblyQualityControlData?.illumina_depth_of_coverage ?? null,
      ontDepth: assemblyQualityControlData?.ont_depth_of_coverage ?? null,
    },
  };
}

function getIlluminaMedianQScorePercentage(scores: number[]) {
  if (scores.length === 0) {
    return 0;
  }
  // This is a magic number defined by ATCC
  const scoreThreshold = 25;
  const numSufficientQScores = scores.filter((score) => score >= scoreThreshold);
  return numSufficientQScores.length / scores.length;
}

function getAmbiguousContent(contigStatistics: ContigStatistics[]): number {
  const sumAmbiguous = contigStatistics.reduce((a, b) => a + b.ambiguous_nucleotide_count, 0);
  const sumLength = contigStatistics.reduce((a, b) => a + b.length, 0);
  if (sumLength === 0) {
    return 0;
  }
  // this returns the value in proportion (not percent)
  return sumAmbiguous / sumLength;
}

type DisplayedDeviceStats = {
  reads: {
    total_reads?: number | null;
    median_quality?: number | null;
  };

  depth?: {
    mean?: number | null;
  };
};

type DisplayedSequencingStats = {
  ont?: DisplayedDeviceStats;
  illumina?: DisplayedDeviceStats;
};

type QualityControlData = {
  sequencing: {
    trimmedReads: number | null;
    medianQScore: number | null;
    medianQScorePercent: number | null;
    ambiguousContent: number | null;
  };
  assembly: {
    completeness: number | null;
    contamination: number | null;
    illuminaDepth: number | null;
    ontDepth: number | null;
  };
};

/** The generalized QC + statistics shape that works for One Codex and ATCC data */
export type Stats = {
  annotationStats?: AnnotationsSummary;
  sequencingStats?: DisplayedSequencingStats;
  qualityControl?: QualityControlData;
};
